import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

const iso = date => date.toISOString().slice(0, 10);
const money = value => `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AutoIndexBacktest() {
  const today = useMemo(() => new Date(), []);
  const initialStart = useMemo(() => { const date = new Date(today); date.setDate(date.getDate() - 90); return date; }, [today]);
  const [form, setForm] = useState({ index: "NIFTY", timeframe: 5, ema_fast: 5, ema_slow: 21, start_date: iso(initialStart), end_date: iso(today) });
  const [state, setState] = useState({ status: "IDLE", progress: 0, message: "Ready" });

  const request = useCallback(async (path, options) => {
    const response = await fetch(`/api${path}`, options);
    const body = await response.json();
    if (!response.ok) throw new Error(body.detail || body.error || "Request failed");
    return body;
  }, []);

  const refresh = useCallback(async () => {
    try { setState(await request("/auto-index/backtest")); } catch (_) { /* admin session may still be starting */ }
  }, [request]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 2500);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const setPeriod = days => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - days);
    setForm(current => ({ ...current, start_date: iso(start), end_date: iso(end) }));
  };

  const run = async () => {
    try {
      setState(await request("/auto-index/backtest", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      }));
    } catch (error) { window.alert(error.message); }
  };

  const downloadExcel = async () => {
    try {
      const response = await fetch("/api/auto-index/backtest/download/trades.json");
      if (!response.ok) throw new Error("Backtest report data is unavailable");
      const report = await response.json();
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(report.trades || []), "Trades");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(report.daily_summary || []), "Daily Summary");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(Object.entries(report.summary || {}).map(([Metric, Value]) => ({ Metric, Value }))), "Summary");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(Object.entries(report.parameters || {}).map(([Parameter, Value]) => ({ Parameter, Value }))), "Parameters");
      XLSX.writeFile(workbook, `Auto_Index_Backtest_${report.parameters?.index || "Index"}_${report.parameters?.start_date || "from"}_${report.parameters?.end_date || "to"}.xlsx`);
    } catch (error) { window.alert(error.message); }
  };

  const running = ["QUEUED", "RUNNING"].includes(state.status);
  const result = state.result;

  return <section className="panel auto-index-backtest">
    <div className="panel-title"><span>Auto Index Backtest <small>NIFTY &amp; SENSEX · index spot · paper only</small></span></div>
    <div className="panel-body">
      <div className="index-backtest-controls">
        <label>Index<select disabled={running} value={form.index} onChange={event => { const index = event.target.value; setForm(current => ({ ...current, index, ema_fast: index === "SENSEX" ? 9 : 5, ema_slow: 21 })); }}><option>NIFTY</option><option>SENSEX</option></select></label>
        <label>Timeframe<input disabled value="5 minutes" /></label>
        <label>Strategy<input disabled value={`EMA ${form.ema_fast}/${form.ema_slow} Flip`} /></label>
        <label>From<input disabled={running} type="date" value={form.start_date} onChange={event => setForm(current => ({ ...current, start_date: event.target.value }))} /></label>
        <label>To<input disabled={running} type="date" value={form.end_date} onChange={event => setForm(current => ({ ...current, end_date: event.target.value }))} /></label>
        <div className="index-backtest-periods"><button disabled={running} onClick={() => setPeriod(90)}>90 Days</button><button disabled={running} onClick={() => setPeriod(180)}>180 Days</button></div>
        <button className="backtest-run" disabled={running || !form.start_date || !form.end_date} onClick={run}>{running ? "Running…" : "Run Backtest"}</button>
      </div>

      <div className="index-backtest-rules">
        <strong>Execution rules</strong>
        <span>Entries 09:20–14:59 · CE above candle body high · PE below candle body low · ≥40-point gap waits for first EMA flip · opposite EMA close exit · 15:12 square-off</span>
      </div>

      <section className="backtest-results-panel">
        <div className="backtest-results-heading"><h4>Backtest Results</h4><span className={`backtest-result-status ${String(state.status || "IDLE").toLowerCase()}`}>{state.status || "IDLE"}</span></div>
        {state.status === "IDLE" && <p className="auto-stock-empty">Select the index, candle interval, EMA strategy and period, then run the backtest.</p>}
        {running && <div className="auto-stock-backtest-progress"><div><span>{state.message}</span><strong>{Number(state.progress || 0)}%</strong></div><progress max="100" value={Number(state.progress || 0)} /></div>}
        {state.status === "FAILED" && <div className="auto-stock-backtest-error"><strong>Backtest failed:</strong> {state.error || state.message}</div>}
        {state.status === "COMPLETED" && result && <div className="auto-stock-backtest-results">
          <div className="index-backtest-metrics">
            <div><span>Trades</span><strong>{result.trades}</strong></div><div><span>Wins</span><strong className="order-success">{result.wins}</strong></div><div><span>Losses</span><strong className="order-failure">{result.losses}</strong></div><div><span>Gross Index Points</span><strong>{Number(result.gross_points || 0).toFixed(2)}</strong></div><div><span>Notional P&amp;L</span><strong className={Number(result.gross_pnl) >= 0 ? "order-success" : "order-failure"}>{money(result.gross_pnl)}</strong></div>
          </div>
          <div className="auto-stock-backtest-meta"><strong>{result.index} · {result.strategy} · {result.timeframe}</strong><span>{result.period}</span></div>
          {!!result.recent_trades?.length && <div className="auto-stock-table-wrap"><table className="auto-stock-table"><thead><tr><th>Date</th><th>Direction</th><th>Gap</th><th>Entry Time</th><th>Entry</th><th>Exit Time</th><th>Exit</th><th>Exit Reason</th><th>Index Points</th><th>Notional P&amp;L</th></tr></thead><tbody>{result.recent_trades.map((row, index) => <tr key={`${row.entry_time}-${index}`}><td>{row.trade_date}</td><td>{row.option_side}</td><td>{row.gap_type}{row.gap_points == null ? "" : ` (${Number(row.gap_points).toFixed(2)})`}</td><td>{new Date(row.entry_time).toLocaleString("en-IN")}</td><td>{Number(row.entry_price).toFixed(2)}</td><td>{new Date(row.exit_time).toLocaleString("en-IN")}</td><td>{Number(row.exit_price).toFixed(2)}</td><td>{String(row.exit_reason).replaceAll("_", " ")}</td><td>{Number(row.gross_points).toFixed(2)}</td><td className={Number(row.gross_pnl) >= 0 ? "order-success" : "order-failure"}>{money(row.gross_pnl)}</td></tr>)}</tbody></table></div>}
          <details><summary>Calculation assumptions</summary><ul>{(result.assumptions || []).map(item => <li key={item}>{item}</li>)}</ul></details>
          <div className="auto-stock-backtest-downloads"><button type="button" onClick={downloadExcel}>Download Excel Report</button></div>
        </div>}
      </section>
    </div>
  </section>;
}
